import {
  ExpandedHuntPackageRecord,
  GeneratedContent,
  Tool,
} from '@cyborg-monorepo/hunter/common'
import { ProgressSegment } from '@hyperkit'

export interface QueryStatusCounts {
  toDo: number
  run: number
  skip: number
}

/**
 * Recursively count a query and all its nested pivots
 */
export function countQueryAndPivots(
  query: GeneratedContent,
  cb: (status: string) => void,
): void {
  if (query.status) {
    cb(query.status)
  }

  if (query.pivots?.length) {
    query.pivots.forEach((pivot) => countQueryAndPivots(pivot, cb))
  }
}

/**
 * Count query statuses across a list of tools
 * (used by environment-scoped views)
 */
export function countQueriesInTools(tools: Tool[]): QueryStatusCounts {
  let toDo = 0
  let run = 0
  let skip = 0

  tools.forEach((tool) => {
    const queries = tool.generatedContent?.Hunt ?? []
    queries.forEach((query) => {
      countQueryAndPivots(query, (status) => {
        if (status === 'To Do') toDo++
        else if (status === 'Run') run++
        else if (status === 'Skip') skip++
      })
    })
  })

  return { toDo, run, skip }
}

/**
 * Count query statuses across an entire hunt package
 * (existing API – unchanged)
 */
export function countQueriesInPackage(
  huntPackage: ExpandedHuntPackageRecord,
): QueryStatusCounts {
  let toDo = 0
  let run = 0
  let skip = 0

  const environments = huntPackage?.analysis ?? []

  environments.forEach((env) => {
    env.tools?.forEach((tool) => {
      const queries = tool.generatedContent?.Hunt ?? []
      queries.forEach((query) => {
        countQueryAndPivots(query, (status) => {
          if (status === 'To Do') toDo++
          else if (status === 'Run') run++
          else if (status === 'Skip') skip++
        })
      })
    })
  })

  return { toDo, run, skip }
}

/**
 * Build progress bar segments from counts
 * (existing API – unchanged)
 */
export function buildQueryProgressSegments(
  counts: QueryStatusCounts,
): ProgressSegment[] {
  return [
    { value: counts.run, color: 'success', label: 'Run' },
    { value: counts.skip, color: 'info', label: 'Skip' },
    { value: counts.toDo, color: 'secondary', label: 'To Do' },
  ]
}

export function totalQueries(counts: QueryStatusCounts): number {
  return counts.toDo + counts.run + counts.skip
}
